import { FC } from 'react';
import CardRadio from '../CardRadio';
import { CommonAttributesProps } from 'screens/AccessExpressV3/accessExpress.types';
import CardReorder from '../CardReorder';
import { SelectedAttributes } from './mobility.types';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { initialSelected } from './utils';

interface GlobalProps extends CommonAttributesProps {
  selected: SelectedAttributes['global'];
  setScope: (scope: SelectedAttributes['global']['scope']) => void;
  setGlobalZone: (
    globalZone: SelectedAttributes['global']['globalZone'],
  ) => void;
  setCoverage: (coverage: SelectedAttributes['global']['coverage']) => void;
  setOrderCarrier: (
    carrier: SelectedAttributes['global']['orderCarrier'],
  ) => void;
}

const Global: FC<GlobalProps> = ({
  handleStep,
  step,
  selected,
  setCoverage,
  setGlobalZone,
  setScope,
  setOrderCarrier,
}) => {
  const onSelectScope = (scope: SelectedAttributes['global']['scope']) =>
    setScope(scope);

  const onSelectCoverage = (
    coverage: SelectedAttributes['global']['coverage'],
  ) => {
    if (coverage === 'yes')
      handleStep && handleStep(step ?? 1, { scrollTo: coverage === 'yes' });
    setCoverage(coverage);
  };

  const onSelectGlobalZone = (
    globalZone: SelectedAttributes['global']['globalZone'],
  ) => {
    handleStep && handleStep(step ?? 1);
    setGlobalZone(globalZone);
  };

  const onResetOrderCarrier = () =>
    setOrderCarrier(initialSelected.global.orderCarrier);

  const onOrderCarrierDone = () => handleStep && handleStep(step ?? 1);

  return (
    <div className="space-y-8">
      <CardRadio
        title="Usage scope"
        directionMessage="Choose Global for international use, or Domestic for use within the U.S."
        horizontal
        options={[
          {
            label: 'Global',
            value: 'global',
          },
          { label: 'Domestic', value: 'domestic' },
        ]}
        containerClass="!gap-5"
        selected={selected.scope}
        onClick={(scope) =>
          onSelectScope(scope as SelectedAttributes['global']['scope'])
        }
      />
      {selected.scope === 'global' ? (
        <CardRadio
          title="Global zone"
          directionMessage="Select the region or zone where the service will be used to determine coverage and pricing."
          options={[
            {
              label: 'EU Zone',
              value: 'eu-zone',
            },
            { label: 'Zone 1', value: 'zone-1' },
            { label: 'Zone 2', value: 'zone-2' },
            { label: 'Zone 3', value: 'zone-3' },
            { label: 'Zone 4', value: 'zone-4' },
          ]}
          selected={selected.globalZone}
          onClick={(globalZone) =>
            onSelectGlobalZone(
              globalZone as SelectedAttributes['global']['globalZone'],
            )
          }
        />
      ) : null}
      {selected.scope === 'domestic' ? (
        <>
          <CardRadio
            title="International coverage required?"
            attributeClassName="!mb-2"
            horizontal
            options={[
              {
                label: 'Yes',
                value: 'yes',
              },
              { label: 'No', value: 'no' },
            ]}
            containerClass="!gap-5"
            selected={selected.coverage}
            onClick={(coverage) =>
              onSelectCoverage(
                coverage as SelectedAttributes['global']['coverage'],
              )
            }
          />
        </>
      ) : null}
      {selected.coverage === 'no' && (
        <div className="relative">
          <CardReorder
            items={selected.orderCarrier}
            setItems={(carrier) =>
              setOrderCarrier(
                carrier as SelectedAttributes['global']['orderCarrier'],
              )
            }
            title="Carrier priority"
            directionMessage="Designate your primary carrier for main coverage. Then choose secondary and tertiary carriers as backups."
            initialItems={['AT&T', 'T-Mobile', 'Verizon']}
          />
          <div className="mt-4 flex gap-2 px-6">
            <GraniteButton
              size="small"
              variant="secondary"
              onClick={onResetOrderCarrier}
            >
              Reset
            </GraniteButton>
            <GraniteButton size="small" onClick={onOrderCarrierDone}>
              Done
            </GraniteButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default Global;
